import { useEffect, useRef } from 'react';
import { UPLOAD_SCENE } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, ImagePrompt, ImageUploadFile, UploadImageType } from '@/app/types';
import { extractTempImageData, uploadFile, uploadTempImages } from '@/app/utils/client';

export const useImg2VideoTempImagesUpload = (
  imagePrompts: Array<{
    id: string;
    prompt: ImagePrompt | null;
    status: 'done' | 'uploading' | 'error' | 'removed';
  } | null>,
  handleImageChange: (idx: number, newImage: ImageUploadFile) => void,
) => {
  const store = useStoreApi();
  const { userInfo, updateFormParams } = useStore((state) => ({
    userInfo: state.userInfo,
    updateFormParams: state.updateFormParams,
  }));
  const processedUploadKeysRef = useRef<Set<string>>(new Set());
  const pendingUploadKeysRef = useRef<Set<string>>(new Set());

  const updatePromptItem = (promptId: string, updater: (currentPrompt: FormImagePrompt) => FormImagePrompt) => {
    const currentImagePrompts = store.getState().img2videoFormParams.imagePrompts;
    updateFormParams(
      'img2video',
      'imagePrompts',
      currentImagePrompts.map((item) => {
        if (!item || item.id !== promptId || !item.prompt) {
          return item;
        }
        return updater(item);
      }),
    );
  };

  const markPromptStatus = (promptId: string, status: 'done' | 'uploading') => {
    updatePromptItem(promptId, (currentPrompt) => ({
      ...currentPrompt,
      status,
    }));
  };

  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const pendingUploads = imagePrompts
      .filter(
        (prompt): prompt is NonNullable<(typeof imagePrompts)[0]> & { prompt: ImagePrompt } =>
          prompt !== null && prompt.prompt !== null && prompt.status === 'done',
      )
      .map((prompt, index) => {
        const tempData = extractTempImageData(prompt.prompt);
        const remoteSelectedUploadKey =
          !tempData && prompt.prompt._tempSelectedImage && !prompt.prompt.selectedImage
            ? `selected:${prompt.id}`
            : null;

        if (!tempData && !remoteSelectedUploadKey) {
          return null;
        }

        const uploadKey = tempData ? `source:${tempData.id}` : remoteSelectedUploadKey;
        if (!uploadKey) {
          return null;
        }

        if (processedUploadKeysRef.current.has(uploadKey) || pendingUploadKeysRef.current.has(uploadKey)) {
          return null;
        }

        return { prompt, index, tempData, uploadKey };
      })
      .filter(
        (
          item,
        ): item is {
          prompt: NonNullable<(typeof imagePrompts)[0]> & { prompt: ImagePrompt };
          index: number;
          tempData: NonNullable<ReturnType<typeof extractTempImageData>> | null;
          uploadKey: string;
        } => item !== null,
      );

    if (pendingUploads.length === 0) {
      return;
    }

    const uploadPromises = pendingUploads.map(async ({ prompt, index, tempData, uploadKey }) => {
      pendingUploadKeysRef.current.add(uploadKey);
      markPromptStatus(prompt.id, 'uploading');

      try {
        if (tempData) {
          const results = await uploadTempImages([tempData]);
          const result = results.get(tempData.id);

          if (!result || !prompt.prompt) {
            console.error('Failed to upload temp image:', tempData.id);
            markPromptStatus(prompt.id, 'done');
            return;
          }

          const { _tempFile, _tempSelectedImage, ...promptWithoutTemp } = prompt.prompt;
          const updatedPrompt: UploadImageType = {
            sourceImage: result.sourceUri,
            sourceImagePreview: prompt.prompt.sourceImagePreview,
            sourceImageWidth: prompt.prompt.sourceImageWidth ?? 0,
            sourceImageHeight: prompt.prompt.sourceImageHeight ?? 0,
            selectedImage: result.selectedUri ?? result.sourceUri,
            selectedImagePreview: result.selectedUri
              ? prompt.prompt.selectedImagePreview || prompt.prompt.sourceImagePreview
              : prompt.prompt.sourceImagePreview,
            selectedImageRange: promptWithoutTemp.selectedImageRange,
            selectedImageWidth: result.selectedUri
              ? prompt.prompt.selectedImageWidth
              : (prompt.prompt.sourceImageWidth ?? 0),
            selectedImageHeight: result.selectedUri
              ? prompt.prompt.selectedImageHeight
              : (prompt.prompt.sourceImageHeight ?? 0),
          };

          handleImageChange(index, {
            id: prompt.id,
            file: updatedPrompt,
            status: 'done',
          });
          processedUploadKeysRef.current.add(uploadKey);
          return;
        }

        const currentPrompt = prompt.prompt;
        if (!currentPrompt?._tempSelectedImage) {
          markPromptStatus(prompt.id, 'done');
          return;
        }

        const selectedUploadResult = await uploadFile({
          file: currentPrompt._tempSelectedImage,
          width: currentPrompt.selectedImageWidth ?? currentPrompt.sourceImageWidth ?? 0,
          height: currentPrompt.selectedImageHeight ?? currentPrompt.sourceImageHeight ?? 0,
          scene: UPLOAD_SCENE.VIDU,
        });

        if (selectedUploadResult.error || !selectedUploadResult.uri) {
          console.error('Failed to upload temp selected image:', prompt.id, selectedUploadResult.error);
          markPromptStatus(prompt.id, 'done');
          return;
        }

        updatePromptItem(prompt.id, (latestPrompt) => {
          if (!latestPrompt.prompt) {
            return latestPrompt;
          }

          const { _tempSelectedImage, ...promptWithoutTempSelectedImage } = latestPrompt.prompt;

          return {
            ...latestPrompt,
            status: 'done',
            prompt: {
              ...promptWithoutTempSelectedImage,
              selectedId: selectedUploadResult.id ?? promptWithoutTempSelectedImage.selectedId,
              selectedImage: selectedUploadResult.uri,
              selectedImagePreview:
                promptWithoutTempSelectedImage.selectedImagePreview ||
                promptWithoutTempSelectedImage.sourceImagePreview,
            },
          };
        });
        processedUploadKeysRef.current.add(uploadKey);
      } finally {
        pendingUploadKeysRef.current.delete(uploadKey);
      }
    });

    Promise.all(uploadPromises).catch((error) => {
      console.error('Failed to upload temp images:', error);
    });
  }, [userInfo, imagePrompts, handleImageChange, store, updateFormParams]);
};
